import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, Calendar, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

export default function YearlyPlanner() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [topics, setTopics] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTopics();
  }, []);

  const fetchTopics = async () => {
    try {
      const response = await api.get('/topics');
      setTopics(response.data);
    } catch (error) {
      console.error('Error fetching topics:', error);
      toast.error('Erro ao carregar tópicos');
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const getEventsForDate = (day, month, year) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const events = [];

    topics.forEach(topic => {
      // Check main study date
      if (topic.scheduled_date && topic.scheduled_date.startsWith(dateStr)) {
        events.push({ type: 'study', topic });
      }

      // Check review dates
      if (topic.review_dates) {
        topic.review_dates.forEach((revDate, index) => {
          if (revDate && revDate.startsWith(dateStr)) {
            events.push({ type: `R${index + 1}`, topic });
          }
        });
      }
    });

    return events;
  };

  const getEventColor = (events) => {
    if (events.length === 0) return '';
    
    const hasStudy = events.some(e => e.type === 'study');
    const hasR1 = events.some(e => e.type === 'R1');
    const hasR2 = events.some(e => e.type === 'R2');
    const hasR3 = events.some(e => e.type === 'R3');
    const hasR4 = events.some(e => e.type === 'R4');

    if (hasR4) return 'bg-red-600';
    if (hasR3) return 'bg-red-500';
    if (hasR2) return 'bg-orange-500';
    if (hasR1) return 'bg-yellow-500';
    if (hasStudy) return 'bg-blue-500';
    return '';
  };

  const renderMiniMonth = (monthIndex) => {
    const daysInMonth = getDaysInMonth(monthIndex, year);
    const firstDay = getFirstDayOfMonth(monthIndex, year);
    const days = [];

    // Empty cells for days before the first day
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="w-5 h-5"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const events = getEventsForDate(day, monthIndex, year);
      const colorClass = getEventColor(events);
      const isToday = new Date().getDate() === day && 
                      new Date().getMonth() === monthIndex && 
                      new Date().getFullYear() === year;

      days.push(
        <div
          key={day}
          className={`w-5 h-5 text-xs flex items-center justify-center rounded-full cursor-pointer
            ${colorClass ? `${colorClass} text-white` : 'hover:bg-slate-100 dark:hover:bg-slate-700'}
            ${isToday && !colorClass ? 'ring-2 ring-blue-500' : ''}
          `}
          title={events.length > 0 ? events.map(e => `${e.type}: ${e.topic.title}`).join('\n') : ''}
          onClick={() => setSelectedMonth(monthIndex)}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  const renderExpandedMonth = (monthIndex) => {
    const daysInMonth = getDaysInMonth(monthIndex, year);
    const firstDay = getFirstDayOfMonth(monthIndex, year);
    const days = [];

    // Empty cells
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-20 border border-slate-100 dark:border-slate-700"></div>);
    }

    // Days
    for (let day = 1; day <= daysInMonth; day++) {
      const events = getEventsForDate(day, monthIndex, year);
      const isToday = new Date().getDate() === day && 
                      new Date().getMonth() === monthIndex && 
                      new Date().getFullYear() === year;

      days.push(
        <div
          key={day}
          className={`h-20 border border-slate-100 dark:border-slate-700 p-1 overflow-hidden
            ${isToday ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
          `}
        >
          <div className={`text-xs font-medium mb-1 ${isToday ? 'text-blue-600' : ''}`}>{day}</div>
          <div className="space-y-0.5">
            {events.slice(0, 3).map((event, idx) => (
              <div
                key={idx}
                className={`text-xs px-1 py-0.5 rounded truncate cursor-pointer
                  ${event.type === 'study' ? 'bg-blue-500 text-white' : ''}
                  ${event.type === 'R1' ? 'bg-yellow-500 text-white' : ''}
                  ${event.type === 'R2' ? 'bg-orange-500 text-white' : ''}
                  ${event.type === 'R3' ? 'bg-red-500 text-white' : ''}
                  ${event.type === 'R4' ? 'bg-red-600 text-white' : ''}
                `}
                onClick={() => navigate(`/topics/${event.topic.id}`)}
                title={event.topic.title}
              >
                {event.type === 'study' ? event.topic.title : `${event.type}: ${event.topic.title}`}
              </div>
            ))}
            {events.length > 3 && (
              <div className="text-xs text-slate-500">+{events.length - 3} mais</div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="yearly-planner-page">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Calendar className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              Planejamento Anual
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => setYear(y => y - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xl font-bold px-4">{year}</span>
            <Button variant="outline" size="icon" onClick={() => setYear(y => y + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-blue-500"></div>
            <span className="text-sm">Estudo</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-yellow-500"></div>
            <span className="text-sm">R1 (24h)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-orange-500"></div>
            <span className="text-sm">R2 (7d)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-500"></div>
            <span className="text-sm">R3 (15d)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-600"></div>
            <span className="text-sm">R4 (30d)</span>
          </div>
        </div>

        {/* Expanded Month View */}
        {selectedMonth !== null && (
          <Card className="mb-6 shadow-lg">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl">{MONTHS[selectedMonth]} {year}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setSelectedMonth(null)}>
                  Fechar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-0">
                {WEEKDAYS.map((day, idx) => (
                  <div key={idx} className="h-8 flex items-center justify-center text-sm font-medium text-slate-500 border-b">
                    {day}
                  </div>
                ))}
                {renderExpandedMonth(selectedMonth)}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 12-Month Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {MONTHS.map((month, idx) => (
            <Card 
              key={idx} 
              className={`cursor-pointer hover:shadow-lg transition-shadow ${selectedMonth === idx ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => setSelectedMonth(idx)}
            >
              <CardHeader className="pb-2 pt-3 px-3">
                <CardTitle className="text-sm font-medium">{month}</CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-3">
                <div className="grid grid-cols-7 gap-0.5">
                  {WEEKDAYS.map((day, i) => (
                    <div key={i} className="w-5 h-4 text-xs text-center text-slate-400">{day}</div>
                  ))}
                  {renderMiniMonth(idx)}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
